import React, { useEffect, useState, useMemo } from 'react';
import type { User } from '@supabase/supabase-js';
import { UserPlus, Users, Loader2 } from 'lucide-react';
import { useFriends, type Friend } from '../hooks/useFriends';
import { useAchievements } from '../hooks/useAchievements';
import { useFriendsFeed } from '../hooks/useFriendsFeed';
import { useWishlist } from '../hooks/useWishlist';
import { useFriendRequests, describeSendRequestError } from '../hooks/useFriendRequests';
import type { WishlistEntry } from '../types/social';
import { Button } from '../components/ui/Button';
import { FriendCard } from '../components/ui/FriendCard';
import { FriendsFeed } from '../components/FriendsFeed';
import { HelpYouCanGive } from '../components/HelpYouCanGive';
import { InviteFriendModal } from '../components/InviteFriendModal';
import { FriendMedalsRow } from '../components/FriendMedalsRow';
// import { NotificationPrompt } from '../components/NotificationPrompt';
import { Toast } from '../components/ui/Toast';
import { UsernameSearch } from '../components/social/UsernameSearch';
import { FriendRequestsSection } from '../components/social/FriendRequestsSection';
import { PENDING_INVITE_TOAST_KEY } from '../pages/InvitePage';
import { useTutorialTarget } from '../components/tutorial/useTutorialTarget';

interface Props {
  user: User;
  myCounts: Record<string, number>;
  allCodes: string[];
}

function computeCompatibility(
  friend: Friend,
  myMissingSet: Set<string>,
  myDuplicatesSet: Set<string>,
  allCodes: string[]
) {
  const friendDuplicates = new Set(allCodes.filter((c) => (friend.counts[c] || 0) >= 2));
  const friendMissing = new Set(allCodes.filter((c) => (friend.counts[c] || 0) === 0));
  const canGet = allCodes.filter((c) => friendDuplicates.has(c) && myMissingSet.has(c));
  const canGive = allCodes.filter((c) => myDuplicatesSet.has(c) && friendMissing.has(c));
  return { canGet, canGive };
}

export function AmigosTab({ user, myCounts, allCodes }: Props) {
  const userName = (user.user_metadata?.full_name as string) || user.email || '';
  const { friends, loading, generateInviteLink, removeFriend, refresh: refreshFriends } = useFriends(
    user.id,
    user.email ?? '',
    userName
  );
  const { catalog } = useAchievements(user.id);
  const { byCode: wishlistByCode } = useWishlist(user.id);
  const {
    incoming,
    outgoing,
    loading: requestsLoading,
    sendRequest,
    acceptRequest,
    rejectRequest,
    cancelRequest,
  } = useFriendRequests(user.id, { onAccepted: () => void refreshFriends() });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const inviteButtonRef = useTutorialTarget('amigos-gerar-link');

  // Mostra toast caso InvitePage tenha sinalizado um pedido recebido.
  useEffect(() => {
    try {
      const msg = localStorage.getItem(PENDING_INVITE_TOAST_KEY);
      if (msg) {
        setToast(msg);
        localStorage.removeItem(PENDING_INVITE_TOAST_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  const friendIds = useMemo(() => friends.map((f) => f.id), [friends]);
  const friendNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const f of friends) map[f.id] = f.name;
    return map;
  }, [friends]);

  const outgoingTargetIds = useMemo(() => outgoing.map((r) => r.to_user_id), [outgoing]);
  const incomingFromIds = useMemo(() => incoming.map((r) => r.from_user_id), [incoming]);

  const { events: feedEvents, loading: feedLoading } = useFriendsFeed({
    userId: user.id,
    friendIds,
    friendNamesById,
  });

  const enrichedFriends = useMemo(() => {
    const myMissingSet = new Set(allCodes.filter((c) => (myCounts[c] || 0) === 0));
    const myDuplicatesSet = new Set(allCodes.filter((c) => (myCounts[c] || 0) >= 2));
    const wishEntries = Object.values(wishlistByCode);

    const enriched = friends.map((f) => {
      const priorityMatches: WishlistEntry[] = wishEntries
        .filter((w) => (f.counts[w.sticker_code] || 0) >= 2)
        .sort((a, b) => a.priority - b.priority);
      const { canGet, canGive } = computeCompatibility(f, myMissingSet, myDuplicatesSet, allCodes);
      return { friend: f, priorityMatches, canGet, canGive };
    });

    return enriched.sort((a, b) => {
      const aHas = a.priorityMatches.length > 0;
      const bHas = b.priorityMatches.length > 0;
      if (aHas || bHas) {
        if (!aHas) return 1;
        if (!bHas) return -1;
        const aTop = a.priorityMatches[0].priority;
        const bTop = b.priorityMatches[0].priority;
        if (aTop !== bTop) return aTop - bTop;
        return b.priorityMatches.length - a.priorityMatches.length;
      }
      return (b.canGet.length + b.canGive.length) - (a.canGet.length + a.canGive.length);
    });
  }, [friends, myCounts, allCodes, wishlistByCode]);

  return (
    <div className="pb-4">
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      <header className="mb-5 px-1">
        <p className="bt-eyebrow text-green-700 mb-2">Galera</p>
        <h1 className="font-display font-bold text-h1 text-ink-900 tracking-[-0.015em]">
          Convida a galera, fecha o álbum.
        </h1>
        <p className="font-body text-body text-ink-700 mt-2">
          Quanto mais amigos no app, mais trocas. Compartilha o link e a gente faz o match.
        </p>
      </header>

      <div className="mb-5 px-1" ref={inviteButtonRef}>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          icon={UserPlus}
          onClick={() => setInviteOpen(true)}
        >
          Convidar amigo
        </Button>
      </div>

      <FriendRequestsSection
        incoming={incoming}
        outgoing={outgoing}
        loading={requestsLoading}
        onAccept={acceptRequest}
        onReject={rejectRequest}
        onCancel={cancelRequest}
        onAcceptedToast={(name) => setToast(`${name} é seu amigo agora.`)}
        onAcceptError={(name) =>
          setToast(`Não foi possível aceitar o pedido de ${name}. Tente novamente em instantes.`)
        }
      />

      <UsernameSearch
        myUserId={user.id}
        friendIds={friendIds}
        outgoingTargetIds={outgoingTargetIds}
        incomingFromIds={incomingFromIds}
        onSendRequest={async (toUserId) => {
          const res = await sendRequest(toUserId);
          if (!res.ok) {
            setToast(describeSendRequestError(res.reason));
          }
          return res;
        }}
        onSent={(name) => setToast(`Pedido enviado para ${name}.`)}
      />

      {/* Desativado até a edge function notify-user estar entregando push.
          Pedir permissão sem entregar notificação queima o opt-in. */}
      {/* <NotificationPrompt user={user} /> */}

      <FriendsFeed events={feedEvents} loading={feedLoading} catalog={catalog} />

      <HelpYouCanGive
        userId={user.id}
        friendIds={friendIds}
        friendNamesById={friendNamesById}
        myCounts={myCounts}
      />

      {loading ? (
        <div className="flex items-center justify-center py-12 text-ink-500 gap-3">
          <Loader2 size={18} className="animate-spin text-green-700" />
          <span className="font-body text-small">Carregando amigos...</span>
        </div>
      ) : friends.length === 0 ? (
        <div className="bg-white border border-dashed border-ink-300 rounded-xl py-12 px-6 text-center mx-1">
          <Users size={40} className="mx-auto text-ink-300 mb-4" strokeWidth={1.75} />
          <p className="font-display font-semibold text-h3 text-ink-700">Sem amigos ainda</p>
          <p className="font-body text-small text-ink-500 mt-1">
            Convida alguém pra começar a trocar.
          </p>
        </div>
      ) : (
        <div className="space-y-3 px-1">
          <p className="bt-eyebrow text-green-700 mb-1">
            Seus amigos · {friends.length}
          </p>
          {enrichedFriends.map(({ friend, priorityMatches }) => (
            <FriendCard
              key={friend.id}
              friend={friend}
              myCounts={myCounts}
              allCodes={allCodes}
              priorityMatches={priorityMatches}
              onRemove={() => removeFriend(friend.id)}
              slotExpanded={
                <FriendMedalsRow
                  friendId={friend.id}
                  friendName={friend.name}
                  catalog={catalog}
                />
              }
            />
          ))}
        </div>
      )}

      <InviteFriendModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        inviterName={userName}
        generateInviteLink={generateInviteLink}
      />
    </div>
  );
}
